import Select from "@/v2-ui/select"
import Dot from "@/v2-backoffice-client/components/dot"
import { visibilityDotVariant } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.utils"
import type { PrivacyVisibilityType } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.types"

export type TenantPrivacyVisibilitySelectProps = {
  value: number
  visibilityTypes: PrivacyVisibilityType[]
  disabled?: boolean
  ariaLabel: string
  onChange(code: number): void
}

// One cell of the per-source table: the visibility of one person field from
// one source. The dot carries the at-a-glance read (neutral hidden → amber
// licensed-only → orange always visible), so a row's exposure is scannable
// without opening anything. The dot + name go through Select.Value so the
// trigger keeps the standard content-left / chevron-right layout.
function TenantPrivacyVisibilitySelect(
  props: TenantPrivacyVisibilitySelectProps,
) {
  const { value, visibilityTypes, disabled, ariaLabel, onChange } = props

  const current = visibilityTypes.find((type) => type.Code === value)

  return (
    <Select
      value={String(value)}
      onValueChange={(next) => {
        onChange(Number(next))
      }}
      disabled={disabled}
    >
      <Select.Trigger
        size="xs"
        aria-label={ariaLabel}
        data-feature-code="UI_BackofficeTenantPrivacy_Policy_Cell"
      >
        <Select.Value>
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <Dot variant={visibilityDotVariant(value)} />
            <span className="truncate">{current?.Name ?? `Code ${value}`}</span>
          </span>
        </Select.Value>
      </Select.Trigger>

      <Select.Content className="max-w-80">
        {visibilityTypes.map((type) => (
          <Select.Item key={type.Code} value={String(type.Code)}>
            <span className="flex items-start gap-2">
              <Dot
                variant={visibilityDotVariant(type.Code)}
                className="mt-1.5 shrink-0"
              />
              <span>
                {type.Name ?? `Code ${type.Code}`}
                {type.Description && (
                  <span className="block text-xs text-muted-foreground">
                    {type.Description}
                  </span>
                )}
              </span>
            </span>
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  )
}

export default TenantPrivacyVisibilitySelect
